import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { Asset } from 'expo-asset';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import {
  DEFAULT_NOTIFICATION_SOUND_ID,
  getNotificationSound,
  isValidNotificationSoundId,
  type NotificationSoundId,
} from '@/constants/notificationSounds';

export type NotificationSoundKind = 'message' | 'notification';

const STORAGE_KEYS: Record<NotificationSoundKind, string> = {
  message: 'notificationSound.message',
  notification: 'notificationSound.notification',
};

interface NotificationSoundState {
  message: NotificationSoundId;
  notification: NotificationSoundId;
}

const initialState: NotificationSoundState = {
  message: DEFAULT_NOTIFICATION_SOUND_ID,
  notification: DEFAULT_NOTIFICATION_SOUND_ID,
};

let cachedState: NotificationSoundState = { ...initialState };
let isHydrated = false;
let hydratePromise: Promise<void> | null = null;
const listeners = new Set<(state: NotificationSoundState) => void>();

function emit() {
  const snapshot = { ...cachedState };
  listeners.forEach((l) => l(snapshot));
}

async function hydrate(): Promise<void> {
  if (isHydrated) return;
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    try {
      const [msg, notif] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEYS.message),
        AsyncStorage.getItem(STORAGE_KEYS.notification),
      ]);
      if (isValidNotificationSoundId(msg)) cachedState.message = msg;
      if (isValidNotificationSoundId(notif)) cachedState.notification = notif;
    } catch (error) {
      console.warn('[useNotificationSound] Failed to hydrate sound prefs', error);
    } finally {
      isHydrated = true;
      emit();
    }
  })();
  return hydratePromise;
}

function resolveAssetUri(asset: unknown): string | null {
  if (!asset) return null;
  if (typeof asset === 'string') return asset;
  if (typeof asset === 'object' && 'uri' in asset && typeof (asset as any).uri === 'string') {
    return (asset as any).uri;
  }
  return Asset.fromModule(asset as any).uri ?? null;
}

// Cache one HTMLAudioElement per URI so repeated triggers reuse the same
// playback context. Without this, each `new Audio(...)` is independent and
// the browser can keep stale ones in its pending playback queue across an
// OS suspend/resume — when several resume at sub-frame offsets you get
// phased/garbled audio.
const audioElementCache = new Map<string, HTMLAudioElement>();

function playSoundById(id: NotificationSoundId | null | undefined) {
  if (Platform.OS !== 'web') return;
  const uri = resolveAssetUri(getNotificationSound(id)?.asset);
  if (!uri) return;

  let el = audioElementCache.get(uri);
  if (!el) {
    el = new Audio(uri);
    el.preload = 'auto';
    audioElementCache.set(uri, el);
  }

  try {
    el.pause();
    el.currentTime = 0;
  } catch {
    // Some browsers throw if the element isn't ready yet — safe to ignore.
  }
  // Browsers reject playback if the user has not interacted with the page
  // yet. We accept this limitation rather than working around it.
  el.play().catch(() => {});
}

async function persistSound(kind: NotificationSoundKind, id: NotificationSoundId): Promise<void> {
  cachedState = { ...cachedState, [kind]: id };
  emit();
  try {
    await AsyncStorage.setItem(STORAGE_KEYS[kind], id);
  } catch (error) {
    console.warn('[useNotificationSound] Failed to persist sound pref', error);
  }
}

export function useNotificationSound() {
  const [state, setState] = useState<NotificationSoundState>(cachedState);
  const isLargeScreen = useIsLargeScreen();
  const isAvailable = Platform.OS === 'web' && isLargeScreen;

  useEffect(() => {
    listeners.add(setState);
    if (!isHydrated) hydrate();
    return () => {
      listeners.delete(setState);
    };
  }, []);

  const play = useCallback(
    (kind: NotificationSoundKind) => {
      if (!isAvailable) return;
      playSoundById(cachedState[kind]);
    },
    [isAvailable]
  );

  const previewSound = useCallback(
    (id: NotificationSoundId) => {
      if (!isAvailable) return;
      playSoundById(id);
    },
    [isAvailable]
  );

  return {
    messageSoundId: state.message,
    notificationSoundId: state.notification,
    isAvailable,
    isHydrated,
    play,
    previewSound,
    setSound: persistSound,
  };
}
